import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    Exercise,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    LabelTelescope,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    TextField,
} from '../../components';
import {
    IllustrationBulb,
    IllustrationDancing,
    IllustrationListening,
    IllustrationRadio,
} from '../../icons';
import { ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseViewProps } from '../exercises';

const page1Text1Key = 'page1Text1';
const page1Text2Key = 'page1Text2';
const page1Text3Key = 'page1Text3';

export const dataKeys = [page1Text1Key, page1Text2Key, page1Text3Key];
export const questions: Question[] = [
    createQuestionFromConfiguration({
        title: 'Hvilke sange kører på repeat hos dig?',
        description: 'Skriv det ned, som din indre DJ spiller igen og igen.',
        dataKeys,
    }),
];

function pickRandomItem<T>(items: T[], defaultValue: T) {
    if (items.length === 0) {
        return defaultValue;
    }

    const rnd = Math.random();
    const index = Math.floor(rnd === 1 ? items.length - 1 : rnd * items.length);
    return items[index] ?? defaultValue;
}

export function Module1S2Exercise1({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Din indre DJ">
            <ProgressBar current={exercise.CurrentPage} total={4} />
            <Page />
        </Exercise>
    );

    function Page() {
        const entries = [
            exercise.Data[page1Text1Key],
            exercise.Data[page1Text2Key],
            exercise.Data[page1Text3Key],
        ].filter(Boolean);

        switch (exercise.CurrentPage) {
            default:
            case 1:
                return (
                    <Page1
                        savedData={{
                            text1: exercise.Data[page1Text1Key] ?? '',
                            text2: exercise.Data[page1Text2Key] ?? '',
                            text3: exercise.Data[page1Text3Key] ?? '',
                        }}
                        onContinue={(data) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                                data: {
                                    [page1Text1Key]: normalizeInput(data.text1),
                                    [page1Text2Key]: normalizeInput(data.text2),
                                    [page1Text3Key]: normalizeInput(data.text3),
                                },
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        entries={entries}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <Page3
                        entries={entries}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 4,
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <Page4
                        entries={entries}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
        }
    }
}

type PageProps = {
    entries: string[];
    onContinue: () => void;
};

type Page1Data = { text1: string; text2: string; text3: string };
type Page1Props = {
    savedData: Page1Data;
    onContinue: (data: Page1Data) => void;
};
function Page1({ savedData, onContinue }: Page1Props) {
    const [text1, setText1] = useSyncedState(savedData.text1);
    const [text2, setText2] = useSyncedState(savedData.text2);
    const [text3, setText3] = useSyncedState(savedData.text3);
    return (
        <>
            <LabelDisplay2>Dine indre greatest hits</LabelDisplay2>
            <LabelBody class="mt-10">
                Du skal nu genkalde dig de tanker, der ofte kan fylde hos dig.
                Forestil dig, at de tanker er sange din indre DJ spiller igen og
                igen, men som ikke er så hjælpsomme.
            </LabelBody>
            <IllustrationDancing className="mx-auto my-30" />
            <Card>
                <LabelDisplay1>
                    Hvilke sange kører på repeat hos dig?
                </LabelDisplay1>
                <LabelBody class="mt-10">
                    Skriv det ned, som din indre DJ spiller igen og igen.
                </LabelBody>
                <TextField
                    className="mt-20"
                    placeholder="F.eks. “Jeg er ikke god nok”"
                    text={text1}
                    onChangeText={setText1}
                />
                <TextField
                    className="mt-20"
                    placeholder="F.eks. “Hvad nu hvis det ikke går?”"
                    text={text2}
                    onChangeText={setText2}
                />
                <TextField
                    className="mt-20"
                    placeholder="F.eks. “Jeg består nok ikke testen”"
                    text={text3}
                    onChangeText={setText3}
                />
                <LabelBody class="mt-20">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton
                onClick={() => {
                    onContinue({ text1, text2, text3 });
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page2({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay1>Træd et skridt tilbage</LabelDisplay1>
            <LabelBody class="mt-10">
                Vi kan alle få overtænkt og bekymret os en masse. Det er ikke
                forkert, men vi kan sidde fast i de samme tanker, uden at det
                hjælper os videre. Det kan hjælpe at træde et skridt tilbage fra
                dem.
            </LabelBody>
            <IllustrationListening className="mx-auto my-30" />
            <SwirlyBox>
                Er du klar til at se nærmere på de titelsange, som din indre DJ
                spiller igen og igen?
            </SwirlyBox>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

function Page3({ entries, onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay1>Dine indre greatest hits</LabelDisplay1>
            <LabelBody class="mb-30 mt-10">
                Du kan skabe afstand fra tanken eller i dette tilfælde{' '}
                {entries.length === 1 ? 'din titelsang' : 'dine titelsange'} ved
                hjælp af den måde du taler om tanken på. Og det kan du gøre
                skridt for skridt.
            </LabelBody>
            {entries.length > 0 && (
                <div className="mb-30">
                    <IllustrationRadio className="float-right" />
                    <LabelDisplay1>
                        {entries.length === 1
                            ? 'Din titelsang'
                            : 'Dine titelsange'}
                    </LabelDisplay1>
                    <LabelBody className="mt-10">
                        <BulletList items={entries} />
                    </LabelBody>
                </div>
            )}
            <Card>
                <LabelDisplay1>Sådan gør du</LabelDisplay1>
                <LabelBody class="mb-20 mt-10">
                    <BulletList
                        items={[
                            'Prøv nu at læse hver enkelt titelsang højt. Start alle sætninger med: “Jeg har den tanke at...” og afslut dem med: “... men det er bare en tanke”.',
                        ]}
                    />
                </LabelBody>
                <LabelBody bold>Et eksempel kan være:</LabelBody>
                <LabelTelescope>
                    Jeg har den tanke at...
                    <br />
                    {pickRandomItem(entries, 'ingen kan lide mig')}
                    <br />
                    ...men det er bare en tanke.
                </LabelTelescope>
                <LabelBody class="mt-20">
                    <BulletList
                        items={[
                            'Du kan skabe endnu mere afstand ved at tilføje en sætning. Prøv at starte med: “Jeg bemærker, (at jeg har den tanke at... men det er bare en tanke)”.',
                        ]}
                    />
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue}>
                OK, det har jeg gjort
            </SecondaryButton>
        </>
    );
}

function Page4({ entries, onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay1>Afrunding</LabelDisplay1>
            <LabelBody class="mt-10">
                Det er helt normalt at blive filtret ind i sine tanker - det
                vigtige er at øve sig i at opdage det, når det sker.
            </LabelBody>
            <LabelBody class="mt-10">
                Når du fremover opdager, at din indre DJ spiller sangene på
                repeat, kan du bruge den øvelse, vi lige har været igennem.
            </LabelBody>
            <LabelBody class="mt-10">
                Du kan sige til dig selv: “nå... jeg bemærker, at jeg har den
                tanke at... {pickRandomItem(entries, 'ingen kan lide mig')}{' '}
                ...men det er bare en tanke”.
            </LabelBody>
            <LabelBody>
                Du kan måske også tænke: “nå.. det er den sang DJ'en prøver at
                spille igen”.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
